'use client';

import { useState, useEffect, useRef, useCallback } from 'react';

const PLATFORMS = ['Google Ads', 'Meta Ads', 'Microsoft Ads', 'TikTok', 'LinkedIn'];

const generateData = (baseValue: number, volatility: number, growth: number, seed: number) => {
    const data = [];
    let value = baseValue;
    for (let i = 0; i < 30; i++) {
        const random = (Math.sin(i * 1.7 + seed) * 0.3 + Math.cos(i * 0.9 + seed) * 0.2) * volatility;
        const trend = i * growth;
        value = Math.max(baseValue * 0.3, value + random + trend * 0.5);
        data.push(Math.round(value));
    }
    return data;
};

const generateROAS = (baseROAS: number, growth: number, seed: number) => {
    const data = [];
    let value = baseROAS;
    for (let i = 0; i < 30; i++) {
        const random = Math.sin(i * 1.3 + seed) * 0.4 + Math.cos(i * 0.7 + seed * 0.5) * 0.3;
        const trend = i * growth * 0.03;
        value = Math.max(0.5, value + random + trend);
        data.push(Math.round(value * 10) / 10);
    }
    return data;
};

const INDUSTRIES = [
    {
        id: 0,
        name: 'Ecommerce',
        sub: 'Fashion — Meta Ads + Google Shopping',
        platforms: ['Meta Ads', 'Google Ads'],
        caption: '30-day campaign performance',
        kpis: [
            { label: 'Ad Spend', value: '$42,800' },
            { label: 'Revenue Generated', value: '$198,600', trend: 'up' },
            { label: 'ROAS', value: '4.64x', trend: 'up' },
            { label: 'CPA', value: '$27', trend: 'down' },
            { label: 'Revenue Growth', value: '+163%', trend: 'up' },
            { label: 'Orders', value: '2,438', trend: 'up' },
        ],
    },
    {
        id: 1,
        name: 'Real Estate',
        sub: 'Luxury Listings — Google Ads + Meta',
        platforms: ['Google Ads', 'Meta Ads'],
        caption: '30-day campaign performance',
        kpis: [
            { label: 'Qualified Leads', value: '486', trend: 'up' },
            { label: 'Cost per Lead', value: '$74', trend: 'down' },
            { label: 'Appointments', value: '128', trend: 'up' },
            { label: 'Closed Deals', value: '18', trend: 'up' },
            { label: 'Pipeline Generated', value: '$6.8M', trend: 'up' },
            { label: 'Avg. Deal Size', value: '$377K' },
        ],
    },
    {
        id: 2,
        name: 'Airport Shuttle',
        sub: 'Rental & Transfer Bookings — Google + Meta',
        platforms: ['Google Ads', 'Meta Ads'],
        caption: '30-day campaign performance',
        kpis: [
            { label: 'Monthly Bookings', value: '2,640', trend: 'up' },
            { label: 'CPA', value: '$18', trend: 'down' },
            { label: 'Booking Rate', value: '12.8%', trend: 'up' },
            { label: 'Revenue', value: '$138K', trend: 'up' },
            { label: 'ROAS', value: '5.1x', trend: 'up' },
            { label: 'Repeat Bookings', value: '31%' },
        ],
    },
    {
        id: 3,
        name: 'HVAC',
        sub: 'Seasonal AC Campaigns — Google Local Services + Microsoft',
        platforms: ['Google Ads', 'Microsoft Ads'],
        caption: '30-day campaign performance',
        kpis: [
            { label: 'Calls', value: '612', trend: 'up' },
            { label: 'Booked Jobs', value: '248', trend: 'up' },
            { label: 'Cost Per Lead', value: '$52', trend: 'down' },
            { label: 'Conversion Rate', value: '40.5%', trend: 'up' },
            { label: 'ROAS', value: '7.6x', trend: 'up' },
            { label: 'Avg. Job Value', value: '$890' },
        ],
    },
    {
        id: 4,
        name: 'Plumbing',
        sub: 'Emergency Service Calls — Google LSA + Meta',
        platforms: ['Google Ads', 'Meta Ads'],
        caption: '30-day campaign performance',
        kpis: [
            { label: 'Calls', value: '742', trend: 'up' },
            { label: 'Booked Jobs', value: '296', trend: 'up' },
            { label: 'Cost Per Lead', value: '$46', trend: 'down' },
            { label: 'Conversion Rate', value: '39.8%', trend: 'up' },
            { label: 'ROAS', value: '8.3x', trend: 'up' },
            { label: 'Avg. Ticket', value: '$612' },
        ],
    },
    {
        id: 5,
        name: 'Moving Company',
        sub: 'Local + Search Booking Funnel — Google Ads + Microsoft',
        platforms: ['Google Ads', 'Microsoft Ads'],
        caption: '30-day campaign performance',
        kpis: [
            { label: 'Leads', value: '511', trend: 'up' },
            { label: 'Cost Per Lead', value: '$63', trend: 'down' },
            { label: 'Booked Moves', value: '184', trend: 'up' },
            { label: 'Revenue', value: '$312K', trend: 'up' },
            { label: 'ROAS', value: '6.2x', trend: 'up' },
            { label: 'Avg. Move Value', value: '$1,695' },
        ],
    },
    {
        id: 6,
        name: 'Healthcare',
        sub: 'Patient Acquisition Funnel — Google Ads + Meta',
        platforms: ['Google Ads', 'Meta Ads'],
        caption: '30-day campaign performance',
        kpis: [
            { label: 'Appointments', value: '982', trend: 'up' },
            { label: 'Cost Per Appointment', value: '$34', trend: 'down' },
            { label: 'Patient Acquisition', value: '418', trend: 'up' },
            { label: 'ROAS', value: '5.4x', trend: 'up' },
            { label: 'Revenue', value: '$520K', trend: 'up' },
            { label: 'New Patient Rate', value: '43%' },
        ],
    },
];

const INDUSTRIES_WITH_DATA = INDUSTRIES.map((ind, idx) => {
    const seed = idx * 2.7 + 1.5;
    const baseInvestment = 1200 + idx * 1800;
    const baseOutcome = 2500 + idx * 4500;
    const baseROAS = 2.2 + idx * 0.4;

    return {
        ...ind,
        investment: generateData(baseInvestment, 600 + idx * 100, 40 + idx * 25, seed),
        outcome: generateData(baseOutcome, 1800 + idx * 300, 80 + idx * 70, seed),
        line: generateROAS(baseROAS, 0.25 + idx * 0.08, seed),
    };
});

const GOLD_COLOR = '#D97706';
const GOLD_DIM = 'rgba(217, 119, 6, 0.15)';

const yFor = (v: number) => 95 - (v / 16) * 75;

export default function Dashboard() {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [secondsSince, setSecondsSince] = useState(0);
    const [progress, setProgress] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [animatedLinePoints, setAnimatedLinePoints] = useState<string>('');
    const [animatedDotPos, setAnimatedDotPos] = useState({ x: 92, y: 50 });
    const [contentKey, setContentKey] = useState(0);

    const rotateRef = useRef<NodeJS.Timeout | null>(null);
    const syncRef = useRef<NodeJS.Timeout | null>(null);
    const progressRef = useRef<NodeJS.Timeout | null>(null);
    const animFrameRef = useRef<number | null>(null);

    const current = INDUSTRIES_WITH_DATA[currentIndex];
    const maxVal = Math.max(...current.investment, ...current.outcome);

    const computeLinePoints = useCallback((lineData: number[]) => {
        const totalDays = lineData.length;
        const xPositions = Array.from({ length: totalDays }, (_, i) => (i / (totalDays - 1)) * 100);
        const yPositions = lineData.map(yFor);
        return {
            pointsStr: xPositions.map((x, i) => `${x},${yPositions[i]}`).join(' '),
            lastX: xPositions[xPositions.length - 1],
            lastY: yPositions[yPositions.length - 1],
            yPositions,
        };
    }, []);

    const animateTransitionTo = useCallback((targetIndex: number) => {
        if (isTransitioning && targetIndex === currentIndex) return;
        setIsTransitioning(true);

        const prevIndex = currentIndex;
        const prevLine = INDUSTRIES_WITH_DATA[prevIndex].line;
        const targetInd = INDUSTRIES_WITH_DATA[targetIndex];

        setCurrentIndex(targetIndex);
        setContentKey(prev => prev + 1);
        setSecondsSince(0);
        setProgress(0);

        const oldY = computeLinePoints(prevLine).yPositions;
        const newY = computeLinePoints(targetInd.line).yPositions;
        const totalDays = targetInd.line.length;
        const xPositions = Array.from({ length: totalDays }, (_, i) => (i / (totalDays - 1)) * 100);

        const dur = 750;
        const start = performance.now();

        const step = (now: number) => {
            const p = Math.min((now - start) / dur, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            const pts = oldY.map((o, i) => o + (newY[i] - o) * eased);
            const pointsStr = xPositions.map((x, i) => `${x},${pts[i]}`).join(' ');

            setAnimatedLinePoints(pointsStr);
            setAnimatedDotPos({
                x: xPositions[xPositions.length - 1],
                y: pts[pts.length - 1],
            });

            if (p < 1) {
                animFrameRef.current = requestAnimationFrame(step);
            } else {
                setIsTransitioning(false);
                const finalPoints = computeLinePoints(targetInd.line);
                setAnimatedLinePoints(finalPoints.pointsStr);
                setAnimatedDotPos({ x: finalPoints.lastX, y: finalPoints.lastY });
            }
        };

        if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
        animFrameRef.current = requestAnimationFrame(step);
    }, [currentIndex, isTransitioning, computeLinePoints]);

    const nextSlide = useCallback(() => {
        const nextIndex = (currentIndex + 1) % INDUSTRIES_WITH_DATA.length;
        animateTransitionTo(nextIndex);
    }, [currentIndex, animateTransitionTo]);

    const startTimers = useCallback(() => {
        if (rotateRef.current) clearInterval(rotateRef.current);
        if (syncRef.current) clearInterval(syncRef.current);
        if (progressRef.current) clearInterval(progressRef.current);

        rotateRef.current = setInterval(nextSlide, 5000);
        syncRef.current = setInterval(() => {
            setSecondsSince((s) => s + 1);
        }, 1000);
        progressRef.current = setInterval(() => {
            setProgress((p) => {
                const nextP = p + 2;
                return nextP >= 100 ? 100 : nextP;
            });
        }, 100);
    }, [nextSlide]);

    const stopTimers = useCallback(() => {
        if (rotateRef.current) { clearInterval(rotateRef.current); rotateRef.current = null; }
        if (syncRef.current) { clearInterval(syncRef.current); syncRef.current = null; }
        if (progressRef.current) { clearInterval(progressRef.current); progressRef.current = null; }
    }, []);

    useEffect(() => {
        const initial = computeLinePoints(current.line);
        setAnimatedLinePoints(initial.pointsStr);
        setAnimatedDotPos({ x: initial.lastX, y: initial.lastY });
        setIsTransitioning(false);
    }, [currentIndex, computeLinePoints, current.line]);

    useEffect(() => {
        startTimers();
        return () => {
            stopTimers();
            if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
        };
    }, [startTimers, stopTimers]);

    const handleSelectIndustry = (idx: number) => {
        if (idx === currentIndex) return;
        stopTimers();
        animateTransitionTo(idx);
        setTimeout(() => {
            startTimers();
        }, 800);
    };

    const roasKpi = current.kpis.find((k) => k.label === 'ROAS');
    const roasTagText = roasKpi ? `ROAS ${roasKpi.value}` : 'ROAS —';

    return (
        <div className="w-full max-w-[1320px] ">
            <div
                className="relative rounded-xl border border-gray-200 bg-white p-4 shadow transition-shadow duration-500 overflow-hidden"
                onMouseEnter={stopTimers}
                onMouseLeave={startTimers}
            >
                {/* Progress bar utilizing the `progress` state */}
                <div 
                    className="absolute top-0 left-0 h-[3px] transition-all duration-100 ease-linear"
                    style={{ width: `${progress}%`, background: GOLD_COLOR }}
                />

                <header className="relative flex items-center justify-between flex-wrap gap-2 mb-8 mt-0.5">
                    <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1 font-mono text-[8px] tracking-wider text-gray-700">
                        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500 shadow-sm" />
                        <span>LIVE</span>
                        <span className="ml-0.5 border-l border-gray-200 pl-2 font-medium text-gray-500">
                            synced {secondsSince}s ago
                        </span>
                    </div>
                    <div className="flex gap-1 flex-wrap">
                        {PLATFORMS.map((p) => {
                            const isActive = current.platforms.includes(p);
                            return (
                                <span
                                    key={p}
                                    style={{
                                        fontSize: '9px',
                                        letterSpacing: '0.04em',
                                        padding: '2px 10px',
                                        borderRadius: '4px',
                                        border: '1px solid',
                                        borderColor: isActive ? GOLD_COLOR : '#E5E7EB',
                                        color: isActive ? 'white' : '#6B7280',
                                        background: isActive ? GOLD_COLOR : '#F9FAFB',
                                        boxShadow: isActive ? `0 0 18px ${GOLD_DIM}` : 'none',
                                        fontWeight: isActive ? 600 : 400,
                                        transition: 'all 0.5s ease',
                                        cursor: 'default',
                                    }}
                                >
                                    {p}
                                </span>
                            );
                        })}
                    </div>
                </header>

                <div className="relative grid grid-cols-1 md:grid-cols-[1fr_1.3fr] gap-4 mb-4">
                    <div>
                        <div
                            className="font-mono text-[10px] tracking-[0.12em] uppercase mb-1.5"
                            style={{ color: GOLD_COLOR }}
                        >
                            Industry {String(currentIndex + 1).padStart(2, '0')} / {INDUSTRIES_WITH_DATA.length}
                        </div>
                        <h1 className=" text-xl font-medium text-gray-900  m-0">
                            {current.name}
                        </h1>
                        <div className="text-[12px] text-gray-600 mb-1.5">
                            {current.sub}
                        </div>
                        <div className="inline-block text-[10.5px] text-gray-500  border-t border-dashed border-gray-200 pt-2 max-w-[200px]">
                            {current.caption}
                        </div>
                    </div>

                    <div className="grid grid-cols-3 gap-1">
                        {current.kpis.map((k, kidx) => {
                            const arrow = k.trend === 'up' ? '▲' : k.trend === 'down' ? '▼' : '';
                            const trendColor = k.trend === 'up' ? 'text-emerald-600' : k.trend === 'down' ? 'text-rose-600' : '';
                            return (
                                <div key={`${contentKey}-${kidx}`} className="bg-white border border-gray-200 rounded px-2.5 py-1">
                                    <div className="text-[8px] tracking-[0.05em] font-medium uppercase text-gray-500 ">{k.label}</div>
                                    <div className="text-sm font-semibold text-gray-900 flex items-baseline gap-1">
                                        {k.value}
                                        {arrow && <span className={`text-[10px] font-bold ${trendColor}`}>{arrow}</span>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="relative border-t border-gray-200 pt-3">
                    <div className="flex gap-4 text-[10px] text-gray-600 font-mono mb-2">
                        <span className="flex items-center gap-1.5">
                            <span className="inline-block w-2.5 h-2.5 rounded-sm bg-gray-400" />
                            Investment
                        </span>
                        <span className="flex items-center gap-1.5">
                            <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: GOLD_COLOR }} />
                            Outcome
                        </span>
                        <span className="flex items-center gap-1.5">
                            <span className="inline-block w-3 h-[2px]" style={{ background: GOLD_COLOR }} />
                            ROAS trend
                        </span>
                    </div>

                    <div className="relative h-[150px]">
                        <div className="relative z-[2] flex justify-between items-end h-full gap-0.5 px-[2%]">
                            {current.investment.map((invVal, i) => {
                                const outVal = current.outcome[i];
                                const hA = Math.max((invVal / maxVal) * 100, 3);
                                const hB = Math.max((outVal / maxVal) * 100, 3);
                                return (
                                    <div key={i} className="flex gap-0.5 items-end h-full flex-1 min-w-[5px]">
                                        <div
                                            className="bar rounded-t-md bg-gray-400 flex-1 min-h-[2px] transition-all duration-800"
                                            style={{ height: `${hA}%` }}
                                        />
                                        <div
                                            className="bar rounded-t-md flex-1 min-h-[2px] transition-all duration-800"
                                            style={{ height: `${hB}%`, background: GOLD_COLOR, boxShadow: `0 0 14px ${GOLD_DIM}` }}
                                        />
                                    </div>
                                );
                            })}
                        </div>

                        <svg
                            className="absolute inset-0 w-full h-full z-[3] pointer-events-none"
                            viewBox="0 0 100 100"
                            preserveAspectRatio="none"
                        >
                            <polyline
                                className="fill-none stroke-[1.6] stroke-linecap-round stroke-linejoin-round transition-colors duration-500"
                                style={{ stroke: GOLD_COLOR, filter: `drop-shadow(0 0 5px ${GOLD_DIM})` }}
                                points={animatedLinePoints}
                            />
                            <circle
                                className="fill-white stroke-2 transition-colors duration-500"
                                r="2"
                                cx={animatedDotPos.x}
                                cy={animatedDotPos.y}
                                style={{ stroke: GOLD_COLOR }}
                            />
                        </svg>

                        <div
                            className="absolute font-mono text-[10px] font-semibold text-gray-900 bg-white border border-gray-200 px-1.5 py-1 rounded-[6px] whitespace-nowrap z-[4] pointer-events-none shadow-sm transition-all duration-75"
                            style={{ left: `${animatedDotPos.x}%`, top: `${animatedDotPos.y}%`, transform: 'translate(6px, -50%)' }}
                        >
                            {roasTagText}
                        </div>
                    </div>

                    <div className="flex justify-between pt-1.5 px-[2%] font-mono text-[8px] text-gray-400">
                        <span>Day 1</span>
                        <span>Day 5</span>
                        <span>Day 10</span>
                        <span>Day 15</span>
                        <span>Day 20</span>
                        <span>Day 25</span>
                        <span>Day 30</span>
                    </div>
                </div>

                <div className="flex flex-col items-center gap-1.5 mt-4">
                    <div className="flex justify-center gap-1.5">
                        {INDUSTRIES_WITH_DATA.map((ind, idx) => {
                            const isActive = idx === currentIndex;
                            return (
                                <button
                                    key={ind.id}
                                    onClick={() => handleSelectIndustry(idx)}
                                    className="transition-all duration-300"
                                    style={{
                                        width: isActive ? '28px' : '8px',
                                        height: '8px',
                                        borderRadius: '100px',
                                        background: isActive ? GOLD_COLOR : '#D1D5DB',
                                        border: 'none',
                                        cursor: 'pointer',
                                        padding: 0,
                                        boxShadow: isActive ? `0 0 12px ${GOLD_DIM}` : 'none',
                                    }}
                                    aria-label={'Show ' + ind.name + ' results'}
                                />
                            );
                        })}
                    </div>
                </div>
            </div>

            <style jsx>{`
                @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=IBM+Plex+Mono:wght@500;600;700&family=Inter:wght@400;500;600&display=swap');

                .font-space {
                    font-family: 'Space Grotesk', sans-serif;
                }
                .font-mono {
                    font-family: 'IBM Plex Mono', monospace;
                }

                .bar {
                    width: 100%;
                    border-radius: 2px 2px 0 0;
                    transition: height 0.8s cubic-bezier(0.4, 0, 0.2, 1), background 0.5s ease;
                    min-height: 2px;
                }

                @media (max-width: 620px) {
                    .grid-cols-3 {
                        grid-template-columns: repeat(2, 1fr) !important;
                    }
                }

                @keyframes pulse {
                    0% {
                        box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.55);
                    }
                    70% {
                        box-shadow: 0 0 0 8px rgba(16, 185, 129, 0);
                    }
                    100% {
                        box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
                    }
                }
                .animate-pulse {
                    animation: pulse 1.8s infinite;
                }
            `}</style>
        </div>
    );
}